import { useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CONDITIONS,
  INSPECTION_AREAS,
  INSPECTION_KINDS,
  INSPECTION_STATUSES,
  conditionInfo,
  inspectionKindLabel,
  type ChangeDirection,
} from "@rentorng/core";
import { api } from "@/lib/api";
import { major, minor, titleCase, useFormat } from "@/lib/format";
import { useApiMutation } from "@/lib/mutations";
import { resolveStaff, searchStaff } from "@/lib/pickers";
import { SearchSelect } from "@/components/search-select";
import { useSession } from "@/lib/session";
import { InfoTip } from "@/components/info-tip";
import { MoneyInput } from "@/components/money-input";
import { IconPlus, IconTrash, IconWrench } from "@/components/icons";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardTitle,
  Detail,
  ErrorState,
  Field,
  Input,
  LinkButton,
  PageHeader,
  Select,
  Spinner,
  Table,
  Textarea,
} from "@/components/ui";
import { IconBetter, IconCamera, IconPrinter, IconWorse } from "./Inspections";
import { IconClipboardCheck } from "@/components/icons";

/**
 * One inspection, reviewed from the office.
 *
 * **The screen is arranged around the question the visit was for**, which is not "what condition is
 * it in" but "what has changed since the last time" — so the comparison sits above the lines rather
 * than under them, and the deteriorations are at the top of it. A list of conditions on its own is
 * an opinion; the same list against the previous visit is evidence, and evidence is what a deposit
 * deduction or a landlord's repair bill actually rests on.
 *
 * Three things about how it saves, because they are easy to change into something worse:
 *
 * - **A grade saves the moment it is chosen.** Somebody grading nineteen lines will not press Save
 *   nineteen times, and a single Save at the bottom loses the lot on a dropped connection halfway
 *   down a stairwell. Notes and deductions save on blur, for the same reason.
 * - **Raising a job is one press and takes nothing from the form.** What the job says, where it is
 *   and how urgent it starts are all composed by `maintenanceDraftFrom` in `@rentorng/core` from
 *   the finding itself — see the endpoint for why there is deliberately nothing here to forge.
 * - **A finding that already has a job says so**, and the button is gone. The server refuses a
 *   second one anyway; this is so nobody has to find that out by pressing it.
 */

interface Item {
  id: string;
  area: string;
  item: string;
  condition: string | null;
  note: string | null;
  deductionAmount: number | null;
  sortOrder: number;
  maintenanceId: string | null;
  photos: Array<{ id: string; url: string; name: string | null }>;
}

interface Change {
  area: string;
  item: string;
  from: string | null;
  to: string | null;
  direction: ChangeDirection;
  steps: number;
}

interface DetailResponse {
  inspection: {
    id: string;
    reference: string | null;
    kind: string;
    status: string;
    scheduledFor: string | null;
    completedAt: string | null;
    inspectorUserId: string | null;
    inspectorName: string | null;
    summary: string | null;
    due: string;
  };
  property: { id: string; name: string; city: string | null; addressLine: string | null } | null;
  unit: { id: string; label: string } | null;
  tenancy: { id: string; reference: string; tenantName: string; startDate: string; endDate: string | null } | null;
  where: string | null;
  items: Item[];
  summary: { total: number; graded: number; findings: number; deductions: number };
  previous: { id: string; reference: string | null; completedAt: string | null } | null;
  changes: Change[];
  comparison: { worse: number; better: number; unchanged: number; unknown: number; added: number; removed: number };
  comparisonSentence: string | null;
  jobs: Array<{ id: string; reference: string; title: string; priority: string; status: string; slaDueAt: string | null }>;
  today: string;
  now: string;
}

const CONDITION_TONE: Record<string, "success" | "info" | "warn" | "danger" | "muted"> = Object.fromEntries(
  CONDITIONS.map((condition) => [condition.value, condition.tone]),
) as Record<string, "success" | "info" | "warn" | "danger" | "muted">;

function ConditionBadge({ condition }: { condition: string | null }) {
  const info = conditionInfo(condition);
  return <Badge tone={CONDITION_TONE[info.value]}>{info.label}</Badge>;
}

/** Which way a line moved. The mark carries it, so the column reads at a glance. */
function DirectionMark({ direction }: { direction: ChangeDirection }) {
  if (direction === "worse") return <span className="text-danger-600"><IconWorse size={17} /></span>;
  if (direction === "better") return <span className="text-brand-600"><IconBetter size={17} /></span>;
  return null;
}

const DIRECTION_WORD: Record<ChangeDirection, string> = {
  worse: "Deteriorated",
  better: "Improved",
  unchanged: "Unchanged",
  unknown: "Not comparable",
  new: "New line",
  gone: "Not inspected this time",
};

export function InspectionDetailPage() {
  const { id = "" } = useParams();
  const { vocab, can } = useSession();
  const fmt = useFormat();
  const queryClient = useQueryClient();
  const editable = can("maintenance.edit");

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["inspection", id],
    queryFn: () => api.get<DetailResponse>(`/inspections/${id}`),
  });

  const [adding, setAdding] = useState(false);
  const [showAllChanges, setShowAllChanges] = useState(false);
  const [inspectorUserId, setInspectorUserId] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [raised, setRaised] = useState<{ reference: string; priority: string; slaHours: number } | null>(null);

  const invalidate = [["inspection", id], ["inspections"], ["maintenance"], ["dashboard"], ["nav-counts"]];

  const updateInspection = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.patch(`/inspections/${id}`, input),
    invalidates: invalidate,
  });

  const updateItem = useApiMutation({
    mutationFn: ({ itemId, ...input }: { itemId: string } & Record<string, unknown>) =>
      api.patch(`/inspections/items/${itemId}`, input),
    invalidates: [["inspection", id], ["inspections"]],
  });

  const addItem = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post(`/inspections/${id}/items`, input),
    invalidates: [["inspection", id], ["inspections"]],
    onSuccess: () => setAdding(false),
  });

  const removeItem = useApiMutation({
    mutationFn: (itemId: string) => api.delete(`/inspections/items/${itemId}`),
    invalidates: [["inspection", id], ["inspections"]],
  });

  const raiseJob = useApiMutation({
    mutationFn: (itemId: string) =>
      api.post<{ reference: string; priority: string; slaHours: number }>(`/inspections/items/${itemId}/maintenance`, {}),
    invalidates: invalidate,
    onSuccess: (job) => setRaised(job),
  });

  /* Multipart, not JSON. A photograph from a phone is two to five megabytes and base64 makes it a
     third bigger on the connection that is already the slow part — the same reason and the same
     shape as `uploadUnitPhotos`. */
  async function uploadPhotos(itemId: string, files: File[]) {
    if (!files.length) return;
    setUploadError(null);
    const form = new FormData();
    for (const file of files) form.append("photos", file);
    try {
      const response = await fetch(`/api/v1/inspections/items/${itemId}/photos`, {
        method: "POST",
        body: form,
        credentials: "include",
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as { error?: string } | null;
        setUploadError(payload?.error ?? "Those photographs did not upload.");
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ["inspection", id] });
    } catch {
      setUploadError("Could not reach the server. Check your connection and try again.");
    }
  }

  if (isLoading) return <Spinner />;
  if (error || !data) return <ErrorState error={error} retry={() => refetch()} />;

  const { inspection, summary, comparison } = data;
  const completed = inspection.status === "completed";
  const interesting = data.changes.filter((change) => change.direction !== "unchanged" && change.direction !== "unknown");
  const shownChanges = showAllChanges ? data.changes : interesting;

  /* The lines in the order they were walked, grouped by room. Grouping in the browser rather than
     asking the server for it: the order is already total (`sort_order` then id), and a group is a
     presentation decision that the printed report makes differently. */
  const areas: Array<[string, Item[]]> = [];
  for (const item of data.items) {
    const last = areas[areas.length - 1];
    if (last && last[0] === item.area) last[1].push(item);
    else areas.push([item.area, [item]]);
  }

  return (
    <div>
      <PageHeader
        title={inspection.reference ?? "Inspection"}
        back={{ to: "/app/inspections", label: "Inspections" }}
        subtitle={
          <>
            {inspectionKindLabel(inspection.kind)} · {data.where ?? "Common parts"} ·{" "}
            {completed ? `completed ${fmt.dateTime(inspection.completedAt)}` : `due ${fmt.date(inspection.scheduledFor)}`}
          </>
        }
        description="What was found, line by line, set against the previous inspection of the same place. Anything found can be turned into a maintenance job without retyping it."
        actions={
          <>
            <LinkButton to={`/app/inspections/${id}/report`} variant="secondary">
              <IconPrinter size={16} /> Report
            </LinkButton>
            {editable && !completed ? (
              <Button
                onClick={() => updateInspection.mutate({ status: "completed" })}
                disabled={updateInspection.isPending}
              >
                Mark as completed
              </Button>
            ) : null}
            {editable && inspection.status === "scheduled" ? (
              <Button variant="secondary" onClick={() => updateInspection.mutate({ status: "in_progress" })}>
                Start
              </Button>
            ) : null}
          </>
        }
      />

      {updateInspection.errorMessage ? <Alert tone="danger">{updateInspection.errorMessage}</Alert> : null}
      {raiseJob.errorMessage ? <Alert tone="danger">{raiseJob.errorMessage}</Alert> : null}
      {uploadError ? <Alert tone="danger">{uploadError}</Alert> : null}
      {raised ? (
        <Alert tone="success" title={`Job ${raised.reference} raised`}>
          Priority {raised.priority}, with a response target of {raised.slaHours} hours. It is on the{" "}
          <Link to="/app/maintenance" className="font-medium underline">
            maintenance list
          </Link>{" "}
          and counts towards the overdue figure on the dashboard once that target passes.
        </Alert>
      ) : null}

      <div className="mb-5 grid gap-5 lg:grid-cols-3">
        {/* ------------------------------------------------------------- where */}
        <Card>
          <CardTitle>Where and who</CardTitle>
          <dl>
            <Detail label={titleCase(vocab.property)}>
              {data.property ? (
                <Link to={`/app/properties/${data.property.id}`} className="text-brand-700 hover:underline">
                  {data.property.name}
                </Link>
              ) : (
                "—"
              )}
            </Detail>
            <Detail label={titleCase(vocab.unit)}>{data.unit?.label ?? "Common parts"}</Detail>
            <Detail label={titleCase(vocab.tenant)}>
              {data.tenancy ? (
                <Link to={`/app/tenancies/${data.tenancy.id}`} className="text-brand-700 hover:underline">
                  {data.tenancy.tenantName}
                </Link>
              ) : (
                "No tenancy"
              )}
            </Detail>
            <Detail label="Kind">{inspectionKindLabel(inspection.kind)}</Detail>
            <Detail label="Date">{fmt.date(inspection.scheduledFor)}</Detail>
            <Detail label="Status">
              {INSPECTION_STATUSES.find((status) => status.value === inspection.status)?.label ?? inspection.status}
            </Detail>
          </dl>

          {editable ? (
            <div className="mt-4 grid gap-3">
              <Field label="Inspector" htmlFor="inspectorUserId">
                <SearchSelect
                  id="inspectorUserId"
                  name="inspectorUserId"
                  value={inspectorUserId ?? inspection.inspectorUserId ?? ""}
                  onChange={(next) => {
                    setInspectorUserId(next);
                    updateInspection.mutate({ inspectorUserId: next || null });
                  }}
                  search={searchStaff}
                  resolve={resolveStaff}
                  placeholder="Not assigned"
                  emptyMessage="Nobody matched."
                />
              </Field>
              <Field label="Kind" htmlFor="kind">
                <Select
                  id="kind"
                  value={inspection.kind}
                  onChange={(event) => updateInspection.mutate({ kind: event.target.value })}
                >
                  {INSPECTION_KINDS.map((kind) => (
                    <option key={kind.value} value={kind.value}>
                      {kind.label}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          ) : null}
        </Card>

        {/* ------------------------------------------------------------ figures */}
        <Card>
          <CardTitle hint="A finding is anything graded poor, damaged or missing — the three grades that lead somewhere. Good and fair are recorded and need nobody.">
            What it found
          </CardTitle>
          <dl>
            <Detail label="Lines">{summary.total}</Detail>
            <Detail label="Graded">
              {summary.graded}
              <span className="text-xs font-normal text-ink-400"> of {summary.total}</span>
            </Detail>
            <Detail label="Findings">
              <span className={summary.findings ? "text-danger-600" : ""}>{summary.findings}</span>
            </Detail>
            <Detail label="Jobs raised">{data.jobs.length}</Detail>
            <Detail label="Deductions">{fmt.money(summary.deductions)}</Detail>
          </dl>
          {summary.graded < summary.total && !completed ? (
            <p className="mt-3 text-xs text-ink-500">
              {summary.total - summary.graded} lines are still ungraded. They will print as “not checked”, which is
              deliberate — an ungraded line must never read as one found in good order.
            </p>
          ) : null}
        </Card>

        {/* ------------------------------------------------------------ summary */}
        <Card>
          <CardTitle>The inspector's summary</CardTitle>
          {editable ? (
            <Textarea
              aria-label="The inspector's summary"
              defaultValue={inspection.summary ?? ""}
              rows={7}
              placeholder="What the visit found overall, in the words a landlord would be shown."
              onBlur={(event) => {
                if (event.target.value !== (inspection.summary ?? "")) {
                  updateInspection.mutate({ summary: event.target.value });
                }
              }}
            />
          ) : (
            <p className="whitespace-pre-wrap text-sm text-ink-600">{inspection.summary || "Nothing recorded."}</p>
          )}
        </Card>
      </div>

      {/* ------------------------------------------------- since the last visit */}
      <Card className="mb-5" padded={false}>
        <div className="flex flex-wrap items-start justify-between gap-3 p-5 pb-3 sm:p-6 sm:pb-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-ink-900">Since the previous inspection</h2>
              <InfoTip>
                Lines are matched on the room and the item together, so “Kitchen · Sink, taps and drainage” finds the
                same line on the previous visit however it was spaced or capitalised. A line nobody checked on either
                visit is reported as not comparable rather than as unchanged — a room nobody entered is not an
                improvement on a damaged one.
              </InfoTip>
            </div>
            {data.previous ? (
              <p className="mt-1 text-sm text-ink-500">
                Against{" "}
                <Link to={`/app/inspections/${data.previous.id}`} className="text-brand-700 hover:underline">
                  {data.previous.reference}
                </Link>
                , completed {fmt.dateTime(data.previous.completedAt)}. {data.comparisonSentence}
              </p>
            ) : (
              <p className="mt-1 text-sm text-ink-500">
                This is the first completed inspection of this place, so there is nothing to compare it with yet. It
                becomes the record the next one is measured against.
              </p>
            )}
          </div>
          {data.previous ? (
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <Badge tone={comparison.worse ? "danger" : "muted"}>{comparison.worse} deteriorated</Badge>
              <Badge tone={comparison.better ? "success" : "muted"}>{comparison.better} improved</Badge>
              <Badge tone="muted">{comparison.added} new</Badge>
              <Button size="sm" variant="secondary" onClick={() => setShowAllChanges((all) => !all)}>
                {showAllChanges ? "Only what changed" : "Show every line"}
              </Button>
            </div>
          ) : null}
        </div>

        {data.previous && shownChanges.length ? (
          <Table
            head={
              <tr>
                <th>Where</th>
                <th>Item</th>
                <th>Was</th>
                <th>Now</th>
                <th>Change</th>
              </tr>
            }
          >
            {shownChanges.map((change) => (
              <tr key={`${change.area}|${change.item}`}>
                <td className="text-ink-500">{change.area}</td>
                <td className="font-medium">{change.item}</td>
                <td>{change.from ? <ConditionBadge condition={change.from} /> : <span className="text-ink-400">—</span>}</td>
                <td>{change.to ? <ConditionBadge condition={change.to} /> : <span className="text-ink-400">—</span>}</td>
                <td className="text-sm text-ink-600">
                  <span className="inline-flex items-center gap-1.5">
                    <DirectionMark direction={change.direction} />
                    {DIRECTION_WORD[change.direction]}
                  </span>
                </td>
              </tr>
            ))}
          </Table>
        ) : data.previous ? (
          <p className="px-6 pb-6 text-sm text-ink-500">Nothing has changed since the previous inspection.</p>
        ) : null}
      </Card>

      {/* --------------------------------------------------------- the findings */}
      <Card padded={false}>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ink-200 p-5 sm:p-6">
          <div className="flex items-center gap-2">
            <IconClipboardCheck size={18} />
            <h2 className="text-base font-semibold text-ink-900">Line by line</h2>
            {/* The scale, explained once. It was printed under every row, which put six hundred
                words of grey guidance between a manager and nineteen lines of data. */}
            <InfoTip>
              <p className="mb-1 font-semibold">How to grade, so two inspectors grade alike</p>
              <ul className="space-y-1">
                {CONDITIONS.map((condition) => (
                  <li key={condition.value}>
                    <strong>{condition.label}</strong> — {condition.help}
                  </li>
                ))}
              </ul>
            </InfoTip>
          </div>
          {editable ? (
            <Button size="sm" variant="secondary" onClick={() => setAdding((open) => !open)}>
              {adding ? "Close" : (<><IconPlus size={15} /> Add a line</>)}
            </Button>
          ) : null}
        </div>

        {adding ? (
          <form
            className="grid gap-4 border-b border-ink-200 bg-ink-50 p-5 sm:grid-cols-4 sm:p-6"
            onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              addItem.mutate({
                area: String(form.get("area")),
                item: String(form.get("item")),
                condition: String(form.get("condition")),
                note: String(form.get("note") || "") || null,
              });
            }}
          >
            {addItem.errorMessage ? (
              <div className="sm:col-span-4">
                <Alert tone="danger">{addItem.errorMessage}</Alert>
              </div>
            ) : null}
            <Field label="Where" htmlFor="new-area" required>
              <Select id="new-area" name="area" defaultValue="General">
                {INSPECTION_AREAS.map((area) => (
                  <option key={area} value={area}>
                    {area}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="What was inspected" htmlFor="new-item" required>
              <Input id="new-item" name="item" required placeholder="Ceiling fan in the second bedroom" />
            </Field>
            <Field label="Condition" htmlFor="new-condition">
              <Select id="new-condition" name="condition" defaultValue="not_checked">
                {CONDITIONS.map((condition) => (
                  <option key={condition.value} value={condition.value}>
                    {condition.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Note" htmlFor="new-note">
              <Input id="new-note" name="note" placeholder="Blades bent; wobbles at speed." />
            </Field>
            <div className="sm:col-span-4">
              <Button type="submit" size="sm" disabled={addItem.isPending}>
                {addItem.isPending ? "Adding…" : "Add the line"}
              </Button>
            </div>
          </form>
        ) : null}

        {areas.length === 0 ? (
          <p className="p-6 text-sm text-ink-500">
            This inspection has no lines yet. Add them here, or schedule the next one from the standard checklist.
          </p>
        ) : (
          areas.map(([area, items]) => (
            <div key={area} className="border-b border-ink-100 last:border-0">
              <div className="bg-ink-50 px-5 py-2 text-xs font-semibold uppercase tracking-wider text-ink-500 sm:px-6">
                {area}
              </div>
              {items.map((item) => (
                <ItemRow
                  key={item.id}
                  item={item}
                  editable={editable}
                  jobReference={data.jobs.find((job) => job.id === item.maintenanceId)?.reference ?? null}
                  onGrade={(condition) => updateItem.mutate({ itemId: item.id, condition })}
                  onNote={(note) => updateItem.mutate({ itemId: item.id, note })}
                  onDeduction={(deductionAmount) => updateItem.mutate({ itemId: item.id, deductionAmount })}
                  onPhotos={(files) => uploadPhotos(item.id, files)}
                  onRaise={() => raiseJob.mutate(item.id)}
                  onRemove={() => removeItem.mutate(item.id)}
                  raising={raiseJob.isPending}
                />
              ))}
            </div>
          ))
        )}
      </Card>
    </div>
  );
}

/**
 * One line of the inspection.
 *
 * Its own component because every one of these holds a file input, and a file input is the one
 * control React cannot re-render from props — a single form for nineteen lines would lose whatever
 * was chosen the moment anything else on the page changed.
 */
function ItemRow({
  item,
  editable,
  jobReference,
  onGrade,
  onNote,
  onDeduction,
  onPhotos,
  onRaise,
  onRemove,
  raising,
}: {
  item: Item;
  editable: boolean;
  jobReference: string | null;
  onGrade: (condition: string) => void;
  onNote: (note: string) => void;
  onDeduction: (amount: string | null) => void;
  onPhotos: (files: File[]) => void;
  onRaise: () => void;
  onRemove: () => void;
  raising: boolean;
}) {
  const fmt = useFormat();
  const picker = useRef<HTMLInputElement>(null);
  const info = conditionInfo(item.condition);

  return (
    <div className="grid gap-3 px-5 py-4 sm:grid-cols-12 sm:items-start sm:px-6">
      <div className="sm:col-span-4">
        <p className="text-sm font-medium text-ink-900">{item.item}</p>
        {!editable ? <div className="mt-1"><ConditionBadge condition={item.condition} /></div> : null}
        {jobReference ? (
          <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-ink-500">
            <IconWrench size={14} /> Job {jobReference} raised
          </p>
        ) : null}
      </div>

      {editable ? (
        <div className="sm:col-span-2">
          <Select
            aria-label={`Condition of ${item.item}`}
            value={info.value}
            onChange={(event) => onGrade(event.target.value)}
            className="text-sm"
          >
            {CONDITIONS.map((condition) => (
              <option key={condition.value} value={condition.value}>
                {condition.label}
              </option>
            ))}
          </Select>
        </div>
      ) : null}

      <div className={editable ? "sm:col-span-3" : "sm:col-span-5"}>
        {editable ? (
          <Input
            aria-label={`Note on ${item.item}`}
            defaultValue={item.note ?? ""}
            placeholder="What exactly, and where."
            className="text-sm"
            onBlur={(event) => {
              if (event.target.value !== (item.note ?? "")) onNote(event.target.value);
            }}
          />
        ) : (
          <p className="text-sm text-ink-600">{item.note || "—"}</p>
        )}

        {item.photos.length ? (
          <div className="mt-2 flex flex-wrap gap-2">
            {item.photos.map((photo) => (
              <a key={photo.id} href={photo.url} target="_blank" rel="noreferrer" className="block">
                <img
                  src={photo.url}
                  alt={photo.name ?? `Photograph of ${item.item}`}
                  className="size-14 rounded-[var(--radius-control)] border border-ink-200 object-cover"
                />
              </a>
            ))}
          </div>
        ) : null}
      </div>

      {/* A deduction belongs to a finding. Nineteen money boxes down a page of sound fittings is
          nineteen invitations to attribute a cost to something nobody is complaining about. */}
      <div className="sm:col-span-1">
        {editable && (info.finding || item.deductionAmount) ? (
          /* The box holds **major** units — the number a person says out loud — and the endpoint
             takes minor, converting with `parseMoney`. So what is sent is the box's own string,
             untouched: converting here as well would multiply by a hundred twice, which is exactly
             how two developer forms sent a twenty-five-million figure as two hundred and fifty
             thousand. `minor()` is used only to decide whether anything actually changed. */
          <MoneyInput
            id={`deduction-${item.id}`}
            aria-label={`Deduction for ${item.item}`}
            defaultValue={major(item.deductionAmount)}
            onBlur={(event) => {
              const typed = (event.target as HTMLInputElement).value;
              if ((minor(typed) ?? null) !== (item.deductionAmount ?? null)) onDeduction(typed || null);
            }}
          />
        ) : item.deductionAmount ? (
          <span className="tabular text-sm">{fmt.money(item.deductionAmount)}</span>
        ) : null}
      </div>

      {editable ? (
        <div className="flex flex-wrap items-center gap-1.5 sm:col-span-2 sm:justify-end">
          <input
            ref={picker}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/avif"
            multiple
            className="hidden"
            onChange={(event) => {
              onPhotos([...(event.target.files ?? [])]);
              event.target.value = "";
            }}
          />
          <Button
            size="sm"
            variant="ghost"
            aria-label={`Add a photograph to ${item.item}`}
            onClick={() => picker.current?.click()}
          >
            <IconCamera size={16} />
          </Button>
          {info.finding && !jobReference ? (
            <Button
              size="sm"
              variant="secondary"
              aria-label={`Raise a job for ${item.item}`}
              disabled={raising}
              onClick={onRaise}
            >
              <IconWrench size={15} />
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" aria-label={`Remove ${item.item}`} onClick={onRemove}>
            <IconTrash size={15} />
          </Button>
        </div>
      ) : null}
    </div>
  );
}
